import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { slides } from './slideData';

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  //Replace with the below script temporarily because of the 4th slide CTA button
  
  /*const getButtonLink = (slide: typeof slides[0]) => {
    switch (slide.ctaText.toLowerCase()) {
      case 'learn more':
        return '/news/2nd-annual-conference-announcement#top';
      case 'join our programs':
        return '/programs/cafp#top';
      case 'learn more':
        return '/about#top';
      case 'explore research':
        return '/research#top';
      case 'partner with us':
        return '/contact#top';
      default:
        return '/contact#top';
    }
  };*/

  const getButtonLink = (slide: typeof slides[0]) => {
  const ctaText = slide.ctaText?.toLowerCase(); // Safe access using optional chaining
  switch (ctaText) {
    case 'learn more':
      return '/news/2nd-annual-conference-announcement#top';
    case 'join our programs':
      return '/programs/cafp#top';
    case 'explore research':
      return '/research#top';
    case 'partner with us':
      return '/contact#top';
    default:
      return '/contact#top'; // Fallback
  }
};


  return (
    <section className="relative h-[calc(100vh-4rem)] mt-16">
      {/* Navigation Arrows */}
      <button
        onClick={handlePrevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={handleNextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <AnimatePresence mode="wait">
        {slides.map((slide, index) => (
          <motion.div
            key={slide.title}
            initial={{ opacity: 0 }}
            animate={{ opacity: currentSlide === index ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0"
            style={{ display: currentSlide === index ? 'block' : 'none' }}
          >
            <div className="relative w-full h-full">
              {slide.images ? (
                <div className="grid grid-cols-3 grid-rows-2 h-full gap-1">
                  <div className="col-span-2 row-span-2 relative">
                    <img
                      src={slide.images[0]}
                      alt={`${slide.title} - Main Image`}
                      className="w-full h-full object-cover"
                      style={{ imageRendering: 'crisp-edges' }}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  </div>
                  <div className="relative">
                    <img
                      src={slide.images[1]}
                      alt={`${slide.title} - Secondary Image`}
                      className="w-full h-full object-cover"
                      style={{ imageRendering: 'crisp-edges' }}
                      loading="lazy"
                    />
                  </div>
                  <div className="relative">
                    <img
                      src={slide.images[2]}
                      alt={`${slide.title} - Third Image`}
                      className="w-full h-full object-cover"
                      style={{ imageRendering: 'crisp-edges' }}
                      loading="lazy"
                    />
                  </div>
                </div>
              ) : (
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                  style={{ imageRendering: 'crisp-edges' }}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              )}
              <div className="absolute inset-0 bg-black/40" />
            </div>
            
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.2, delay: 0.3 }}
                  className="max-w-2xl"
                >
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                    {slide.title}
                  </h1>
                  <p className="text-lg text-gray-200 mb-6">
                    {slide.subtitle}
                  </p>
                  
                  {slide.ctaText && (
                  <Link 
                    to={getButtonLink(slide)}
                    onClick={handleScrollToTop}
                    className="inline-flex items-center px-6 py-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
                  >
                    {slide.ctaText}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'w-8 bg-white' : 'w-1.5 bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
}